import styled from "styled-components";
import { useEffect, useMemo, useState } from "react";
import { AccountingReport } from "../../../redux/apiSpecifications/apiFesf";
import WidgetHeader from "../../Widgets/WidgetHeader";
import { useTranslation } from "react-i18next";
import { WidgetWrapper } from "../../../style/DashbordWidgetWrappers";
import { MaterialReactTable, MRT_ColumnDef } from "material-react-table";
import { CellWrapper } from "../../../style/CellWrapper";
import { dateToDisplayDate } from "../../../utils/date";
import ReportActions from "./ReportActions";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { MRT_Localization_IT } from "material-react-table/locales/it";
import { MRT_Localization_EN } from "material-react-table/locales/en";
import { components } from "../../../config/testIds";

const { wrapper: wrapperTestId } = components.menuContent.manageReport.simulatedReportsList;

const SimulatedReportsListWrapper = styled(WidgetWrapper)`
  flex-direction: column;
  align-items: stretch;
  max-height: 2.5em;
  transition: max-height 1s;
  &.open {
    max-height: 100em;
  }

  div.body {
    padding: 1em 2em;

    .MuiPaper-root {
      box-shadow: none;
      width: 95%;
    }

    .MuiInputBase-root {
      &:before {
        border-bottom: 1px solid white;
      }
    }

    .MuiToolbar-root > .MuiBox-root {
      justify-content: flex-start;
    }

    .MuiInputBase-input {
      color: #555;
    }

    .MuiTableCell-root {
      padding: 0.5em 1em;
    }
  }
`;

interface ISimulatedReportsListProps {
  reports: AccountingReport[];
}

const SimulatedReportsList = ({ reports }: ISimulatedReportsListProps) => {
  const { t } = useTranslation();

  const { language } = useSelector((state: RootState) => state.user);

  const [isOpen, setIsOpen] = useState(reports.length > 0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 5,
  });

  useEffect(() => {
    setIsOpen(reports.length > 0);
  }, [reports]);

  const columns = useMemo<MRT_ColumnDef<AccountingReport>[]>(
    () => [
      {
        accessorKey: "name",
        header: t("report-list-name-column-header"),
        size: 100,
        Cell: ({ row }) => <CellWrapper>{row.original.name}</CellWrapper>,
      },
      {
        accessorKey: "startDate",
        header: t("report-list-start-date-column-header"),
        size: 100,
        Cell: ({ row }) => (
          <CellWrapper>{dateToDisplayDate(row.original.startDate)}</CellWrapper>
        ),
      },
      {
        accessorKey: "endDate",
        header: t("report-list-end-date-column-header"),
        size: 100,
        Cell: ({ row }) => (
          <CellWrapper>{dateToDisplayDate(row.original.endDate)}</CellWrapper>
        ),
      },
      {
        accessorKey: "creationDate",
        header: t("report-list-creation-date-column-header"),
        size: 100,
        Cell: ({ row }) => (
          <CellWrapper>
            {dateToDisplayDate(row.original.creationDate)}
          </CellWrapper>
        ),
      },
      {
        accessorKey: "actions",
        header: t("report-list-actions-column-header"),
        size: 100,
        enableSorting: false,
        enableColumnDragging: false,
        enableColumnActions: false,
        Cell: ({ row }) => (
          <ReportActions
            reportId={row.original.id!}
            generationStatus={row.original.generationStatus!}
            reportStatus={row.original.reportStatus!}
            canReview={false}
            canClose={false}
          />
        ),
      },
    ],
    [reports, language]
  );

  const handleOnOpen = () => setIsOpen(true);
  const handleOnClose = () => setIsOpen(false);

  return (
    <SimulatedReportsListWrapper
      data-testid={wrapperTestId}
      className={`${isOpen ? "open" : ""}`}
    >
      <WidgetHeader
        title={t("manage-report-simulated-report-title")}
        isOpen={isOpen}
        isActive={false}
        onOpen={handleOnOpen}
        onClose={handleOnClose}
      />

      <div className="body">
        {reports.length > 0 && (
          <MaterialReactTable
            muiTablePaperProps={{ style: { width: "100%" } }}
            columns={columns}
            data={reports}
            enableDensityToggle={false}
            enableColumnOrdering
            localization={
              language === "it" ? MRT_Localization_IT : MRT_Localization_EN
            }
            muiTableBodyCellProps={{
              sx: {
                textAlign: "left",
              },
            }}
            onPaginationChange={setPagination}
            state={{ pagination }}
            autoResetPageIndex={false}
          />
        )}
      </div>
    </SimulatedReportsListWrapper>
  );
};

export default SimulatedReportsList;
